import { api, apiForm, download } from './client'
import type { AttendanceRecord } from './attendance'
import type { OrgUiConfig } from './auth'
import type { LeaveConfig } from './leave'

/** 오늘 현황 — 전부 사실 집계다. 판정이 아니다 */
export interface TodaySummary {
  totalEmployees: number
  /** 출근을 찍은 사람 */
  presentEmployees: number
  /** 퇴근까지 찍은 사람 */
  checkoutEmployees: number
  /** 출근만 찍고 퇴근 전인 사람 */
  workingEmployees: number
  pendingManualEmployees: number
  pendingManualRecords: number
  /** 아직 아무 기록이 없는 사람. "결근" 이 아니라 "기록 없음" 이다 */
  absentEmployees: number
  absenceTotal: number
  absenceBreakdown: Record<string, number>
}

export function getTodaySummary() {
  return api<TodaySummary>('/api/attendance/today-summary')
}

export interface UntaggedToday {
  count: number
  employees: {
    id: string
    name: string
    employeeNumber: string | null
    departmentName: string | null
  }[]
}

/**
 * 오늘 아무 기록도 없는 직원 명단.
 * today-summary 의 absentEmployees 와 같은 기준으로 세지만, 이쪽은 사람을 준다.
 * "결근"이 아니라 "아직 기록 없음"이다. 판정하지 않는다.
 */
export function getUntaggedToday() {
  return api<UntaggedToday>('/api/attendance/untagged-today')
}

export interface Department {
  id: string
  name: string
  /**
   * 재직(활성) 인원만. 화면에 "N명" 으로 보여줄 값이다.
   *
   * ⚠ 아래 totalCount 와 헷갈리지 말 것. 서버가 일부러 둘로 나눠서 준다
   *   (`employee.service.ts` listDepartments).
   */
  _count: { employees: number }
  /**
   * 퇴사자까지 포함한 전체. **삭제 가능 여부는 이 값으로 판단한다.**
   *
   * 서버는 퇴사자가 한 명이라도 있으면 삭제를 거부한다. 퇴사자가 출퇴근 기록을
   * 물고 있어서, 부서가 사라지면 그 기록의 소속이 끊기기 때문이다.
   * 그래서 화면에서 `_count.employees` 로 판단하면 **0명으로 보이는데 삭제가 실패한다.**
   */
  totalCount: number
}

export function getDepartments() {
  return api<Department[]>('/api/employees/departments')
}

// 부서 생성·수정·삭제는 전부 **ADMIN 전용**이다.
// 라우트에는 authMiddleware 만 붙어 있어 얼핏 열려 보이지만,
// 권한 검사는 컨트롤러 안에 있다 (`employee.controller.ts` create/update/deleteDepartment).
// 화면에서도 ADMIN 에게만 보여주지만(RequireAdmin), 서버가 마지막 방어선이다.

export function createDepartment(name: string) {
  return api<{ id: string; name: string }>('/api/employees/departments', {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

export function updateDepartment(id: string, name: string) {
  return api<{ id: string; name: string }>(`/api/employees/departments/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  })
}

/** 소속 직원이 있으면 서버가 거부한다. 화면에서 미리 막되, 메시지는 서버 것을 그대로 보여준다. */
export function deleteDepartment(id: string) {
  return api<{ message: string }>(`/api/employees/departments/${id}`, { method: 'DELETE' })
}

export interface EmployeeRow {
  id: string
  name: string
  employeeNumber: string | null
  email: string | null
  role: 'ADMIN' | 'MANAGER' | 'EMPLOYEE'
  isActive: boolean
  leftAt: string | null
  mustChangePassword: boolean
  privacyConsentedAt: string | null
  department?: { name: string } | null
  position?: { name: string } | null
}

/**
 * 직원 목록.
 *
 * ⚠️ 응답은 배열이 아니라 `{ employees, total, activeCount, inactiveCount }` 다.
 *    근태와 달리 **서버에 search·departmentId·status 필터가 있다** — 화면에서 거르지 말 것.
 *
 * 2026-08-04: 이 응답에 passwordHash·fcmToken 이 실려 나가던 것을 서버에서 제거했다
 *    (운영 버그로그 참조). 여기서 다시 받지 않도록 타입에도 두지 않는다.
 */
export interface EmployeeListResult {
  employees: EmployeeRow[]
  total: number
  activeCount: number
  inactiveCount: number
}

export function getEmployees(
  params: { take?: number; skip?: number; search?: string; departmentId?: string; status?: 'active' | 'inactive' | 'all' } = {},
) {
  const q = new URLSearchParams()
  q.set('take', String(params.take ?? 50))
  if (params.skip) q.set('skip', String(params.skip))
  if (params.search) q.set('search', params.search)
  if (params.departmentId) q.set('departmentId', params.departmentId)
  if (params.status) q.set('status', params.status)
  return api<EmployeeListResult>(`/api/employees?${q.toString()}`)
}

/**
 * 임시 비밀번호 발급 — **ADMIN 전용**.
 * 부서장에게 열어주면 대리출석 체인이 열린다: 부서장이 부서원 비번을 발급받아
 * 그 계정으로 로그인 → 대신 태깅. 서버도 막지만 화면에서도 노출하지 않는다.
 */
/** 직원 상세 — 목록(EmployeeRow)보다 부서·직급·근무유형 객체가 더 붙어 온다 */
export interface EmployeeDetail extends EmployeeRow {
  organizationId: string
  hireDate: string
  leftAt: string | null
  passwordChangedAt: string | null
  tempPasswordExpiresAt: string | null
  departmentId: string | null
  positionId: string | null
  createdAt: string
  updatedAt: string
  position?: { id: string; name: string } | null
}

export function getEmployee(id: string) {
  return api<EmployeeDetail>(`/api/employees/${id}`)
}

/**
 * 이 직원이 부서장을 맡는 부서들.
 *
 * ⚠ **역할과 이 매핑은 둘 다 있어야 한다.** MANAGER 로 바꾸기만 하고 매핑을 안 넣으면
 *   `scope.ts` 가 빈 배열을 주는데, 빈 배열은 "전체" 가 아니라 "아무것도" 다.
 *   출퇴근·직원·부서·휴가 승인이 전부 빈 화면이 된다 (2026-08-31 까지 실제 상태였다).
 */
export function getManagedDepartments(employeeId: string) {
  return api<{ id: string; name: string }[]>(`/api/employees/${employeeId}/managed-departments`)
}

export function updateManagedDepartments(employeeId: string, departmentIds: string[]) {
  return api<{ id: string; name: string }[]>(`/api/employees/${employeeId}/managed-departments`, {
    method: 'PUT',
    body: JSON.stringify({ departmentIds }),
  })
}

/**
 * 이름·사번·역할·부서 수정. 서버 UpdateEmployeeSchema 와 같은 필드만 받는다.
 *
 * ★ `managedDepartmentIds` 는 **역할을 MANAGER 로 바꿀 때** 같이 보낸다.
 *   서버는 역할이 바뀌면 매핑을 항상 비우고, MANAGER 일 때만 이 값으로 다시 채운다.
 *   따로 두면 한쪽만 하고 끝낼 수 있어서 한 요청으로 묶었다.
 */
export function updateEmployee(
  id: string,
  body: {
    name?: string
    employeeNumber?: string | null
    role?: EmployeeRow['role']
    departmentId?: string | null
    managedDepartmentIds?: string[]
  },
) {
  return api<EmployeeDetail>(`/api/employees/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

export interface EmployeeDevice {
  id: string
  deviceId: string
  deviceName: string | null
  platform: string | null
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'REVOKED'
  isActive: boolean
  approvedAt: string | null
  revokedAt: string | null
  lastUsedAt: string | null
  createdAt: string
}

/**
 * 이 직원의 기기 이력.
 * 1기기 1사용자가 대리출근 차단의 뼈대라, 승인된 기기가 몇 개인지가 곧 보안 상태다.
 */
export function getEmployeeDevices(employeeId: string) {
  return api<EmployeeDevice[]>(`/api/devices/employee/${employeeId}`)
}

export function revokeDevice(deviceId: string) {
  return api<unknown>(`/api/devices/${deviceId}/revoke`, { method: 'PATCH' })
}

/**
 * 기기 승인·반려 (2026-09-07).
 *
 * 🔴 **승인은 그 직원의 기존 활성 기기를 전부 끈다** (`device.service.ts:138`).
 *   그게 1기기 1사용자의 실행 지점이다. 화면은 그 사실을 사용자에게 말해 줘야 한다.
 *
 * ⚠ 서버가 ADMIN 아니면 403 이다 (`device.controller.ts:221`). 화면의 버튼 숨김은
 *   **거들 뿐 문지기가 아니다.** 403 이 오면 서버 문구를 그대로 보여준다.
 */
export function approveDevice(deviceId: string) {
  return api<unknown>(`/api/devices/${deviceId}/approve`, { method: 'PATCH' })
}

/** 승인 대기 기기 한 건 — 목록에서 쓸 만큼만 (`device.service.ts` getPendingDevices) */
export interface PendingDevice extends EmployeeDevice {
  employeeId: string
  employee: {
    name: string
    email: string | null
    employeeNumber: string | null
    department: { name: string } | null
  }
}

/**
 * 조직의 승인 대기 기기 전부 — 오래 기다린 순.
 *
 * 🔴 **ADMIN 전용이다** (`device.controller.ts:90` — 아니면 403).
 *   같은 화면(`/admin/approvals`)에 부서장도 들어오므로, 부르는 쪽에서
 *   `enabled: isAdmin` 으로 **요청 자체를 막아야 한다.** 안 그러면 화면엔 아무것도
 *   없는데 요청만 나가고, 그 403 이 캡처 스펙의 4xx 수집에 잡혀 다음 사람이
 *   «BASIC 의 403 처럼 정상» 으로 오해한다.
 *
 * ⚠ 기기 목록(`/employee/:id`)과 달리 이쪽은 MANAGER 를 아예 안 받는다. 축이 다르다 —
 *   가드레일 #5 는 플랜 축만 보므로 이건 안 잡아 준다.
 */
export function getPendingDevices() {
  return api<PendingDevice[]>('/api/devices/pending')
}

export function rejectDevice(deviceId: string) {
  return api<unknown>(`/api/devices/${deviceId}/reject`, { method: 'PATCH' })
}

export function issueTempPassword(employeeId: string) {
  return api<{ tempPassword: string; expiresAt: string }>(
    `/api/employees/${employeeId}/issue-temp-password`,
    { method: 'POST' },
  )
}

/**
 * 직원 등록 — **ADMIN 전용** (서버도 403).
 *
 * 초기 비밀번호는 보내지 않는다. 서버가 조직 규칙대로 정한다 —
 * 사번이 있으면 사번, 없으면 organization.initialPasswordFallback.
 * 화면에서 임의로 정하면 조직마다 규칙이 갈리고, 그 값이 브라우저를 거쳐 간다.
 */
export function createEmployee(body: {
  name: string
  hireDate: string
  employeeNumber?: string
  email?: string
  role?: EmployeeRow['role']
  departmentId?: string | null
}) {
  return api<EmployeeRow>('/api/employees', { method: 'POST', body: JSON.stringify(body) })
}

/** 퇴사 처리 — ADMIN 전용. 서버가 세션(refreshToken)까지 끊고 푸시를 끈다 */
export function terminateEmployee(id: string) {
  return api<{ message: string }>(`/api/employees/${id}/terminate`, { method: 'POST' })
}

/** 퇴사 복구 — 실수로 처리한 경우 */
export function restoreEmployee(id: string) {
  return api<{ message: string }>(`/api/employees/${id}/restore`, { method: 'POST' })
}

/** 직원 일괄 등록용 빈 양식. 헤더를 설명하는 것보다 채울 파일을 주는 게 낫다 */
export function downloadEmployeeTemplate() {
  return download('/api/export/employee-template', '직원등록_양식.xlsx')
}

/**
 * 엑셀 일괄 등록 — multipart 라 api() 를 쓰지 않는다.
 * Content-Type 을 직접 넣지 않는 것이 중요하다. FormData 는 boundary 를 포함한
 * 값을 브라우저가 붙여야 하는데, 손으로 지정하면 boundary 가 빠져 서버가 파싱을 못 한다.
 */
export function bulkCreateEmployees(file: File) {
  const form = new FormData()
  form.append('file', file)
  return apiForm<BulkResult>('/api/employees/bulk', form)
}

export interface BulkResult {
  total: number
  success: number
  failed: number
  /** 실패한 행만 담긴다. 행 번호는 엑셀 기준(헤더가 1행) */
  errors: { row: number; email: string; name: string; error?: string }[]
}

/** 직원 정보가 붙은 근태 레코드 — 관리자 화면용 */
export interface AdminAttendanceRecord extends AttendanceRecord {
  employeeId: string
  employee?: { id: string; name: string; employeeNumber: string | null } | null
  /** 기록 시점의 부서 (스냅샷). 부서를 옮겨도 과거 기록은 그때 부서로 남는다 */
  departmentDisplay?: string | null
  departmentSnapshotName?: string | null
  isHistoricalDepartment?: boolean
}

/**
 * 조직 근태 조회.
 *
 * ⚠️ 서버에 부서 필터가 없다 (getRecords 는 employeeId·from·to·skip·take 만 받는다).
 *    부서 좁히기는 화면에서 departmentDisplay 로 한다. 조직이 커지면 서버 필터가
 *    필요해질 자리다 — take 상한에 걸려 일부만 보이던 사고가 실제로 있었다.
 */
export function getOrgAttendance(params: { from: string; to: string; employeeId?: string; take?: number }) {
  const q = new URLSearchParams()
  /*
   * 날짜만 보내면 안 된다. 서버는 `new Date('2026-08-24')` 로 파싱해 **UTC 자정**으로 본다.
   * from==to (오늘 필터) 면 범위가 한 순간이 되어 **0건**이 나온다 — 실제로 그랬다.
   * 여러 날 범위여도 마지막 날 09:00 KST 이후가 잘린다.
   * (getMyAttendance 도 같은 문제였다. 2026-08-24 함께 수정)
   */
  q.set('from', `${params.from}T00:00:00+09:00`)
  q.set('to', `${params.to}T23:59:59.999+09:00`)
  if (params.employeeId) q.set('employeeId', params.employeeId)
  q.set('take', String(params.take ?? 1000))
  // 야간조 고아 퇴근에 "직전 기록" 을 붙여 달라는 표시. **React 만 보낸다** —
  // 같은 API 를 DCMC(Svelte)도 쓰는데 그쪽은 동결 대상이고, 켜면 쿼리가 더 나간다.
  q.set('withPrevTag', '1')
  return api<AdminAttendanceRecord[]>(`/api/attendance?${q.toString()}`)
}

/** 승인 대기 중인 수기·부재 */
export function getManualPending() {
  return api<AdminAttendanceRecord[]>('/api/attendance/manual-pending')
}

export function approveManual(id: string) {
  return api<AdminAttendanceRecord>(`/api/attendance/${id}/approve`, { method: 'PATCH' })
}

export function rejectManual(id: string, rejectionReason: string) {
  return api<AdminAttendanceRecord>(`/api/attendance/${id}/reject`, {
    method: 'PATCH',
    body: JSON.stringify({ rejectionReason }),
  })
}

// ── NFC 근무지 (태그판) ────────────────────────────────────────
// 전 동작이 ADMIN 전용이다 (라우터 requireAdmin + 컨트롤러 이중 방어).

export interface NfcSpot {
  id: string
  name: string
  location: string | null
  type: string
  /** 태그판 하드웨어 UID. 비어 있으면 어떤 태그를 대도 매칭되지 않는다 */
  nfcUid: string | null
  isActive: boolean
  todayTagCount: number
  lastTag: { employeeName: string; timestamp: string } | null
  createdAt: string
}

export function getNfcSpots() {
  return api<NfcSpot[]>('/api/nfc-spots')
}

export function createNfcSpot(body: { name: string; location?: string }) {
  return api<NfcSpot>('/api/nfc-spots', { method: 'POST', body: JSON.stringify(body) })
}

export function updateNfcSpot(
  id: string,
  body: { name?: string; location?: string; nfcUid?: string | null; isActive?: boolean },
) {
  return api<NfcSpot>(`/api/nfc-spots/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
}

/** 삭제가 아니라 비활성화다 — 과거 기록이 가리키는 스팟은 남아야 한다 */
export function deactivateNfcSpot(id: string) {
  return api<{ message: string }>(`/api/nfc-spots/${id}`, { method: 'DELETE' })
}

// ── 보안 (전부 ADMIN 전용, requireAdmin) ───────────────────────
// 시프티는 Audit Log 를 Enterprise 티어에 둔다. 우리는 BASIC 에 넣는다 —
// "기록이 조작되지 않았음" 이 이 제품이 파는 것이기 때문이다.

export interface ChainVerifyResult {
  ok: boolean
  totalRows: number
  /** 해시가 있는 row (fallback 제외) */
  chainedRows: number
  /** genesis 부터 실제로 이어진 수. chainedRows 와 다르면 끊긴 것 */
  walkedRows: number
  genesisCount: number
  chainBroken: boolean
  /** 앱을 거치지 않고 바뀐 흔적 */
  tamperedCount: number
  tamperedRows: { id: string; createdAt: string }[]
  /** 관리자가 앱에서 정상 수정한 것 — 변조가 아니다 */
  approvedEditCount: number
  approvedEditRows: { id: string; createdAt: string }[]
  /** 체인 계산에 실패해 hash=null 로 들어간 row (CHAIN_FAILED) */
  fallbackCount: number
  summary: string
}

export function verifyChain() {
  return api<ChainVerifyResult>('/api/security/chain-verify')
}

export interface AnomalyAlert {
  id: string
  type: string
  message: string | null
  metadata: unknown
  resolvedAt: string | null
  createdAt: string
  employee?: { name: string; employeeNumber: string | null } | null
}

export function getAnomalies(params: { resolved?: boolean; take?: number } = {}) {
  const q = new URLSearchParams()
  if (params.resolved !== undefined) q.set('resolved', String(params.resolved))
  if (params.take) q.set('take', String(params.take))
  return api<{ items: AnomalyAlert[]; total: number; typeCounts: Record<string, number> }>(
    `/api/security/anomalies?${q.toString()}`,
  )
}

export function resolveAnomaly(id: string) {
  return api<AnomalyAlert>(`/api/security/anomalies/${id}/resolve`, { method: 'PATCH' })
}

export interface AuditLogItem {
  id: string
  action: string
  targetType: string | null
  targetId: string | null
  metadata: unknown
  createdAt: string
  /** 서버가 붙여주는 사람이 읽는 라벨 — 원본 ID 를 그대로 보여주지 않기 위한 것 */
  actorLabel?: string | null
  targetLabel?: string | null
}

/**
 * 표준 엑셀 1종 — 집계(출근일수) + 상세 2시트.
 * from·to 는 KST 날짜(YYYY-MM-DD)이고 to 는 그 날을 포함한다 (서버가 다음날 0시 미만으로 바꾼다).
 */
export function downloadStandardExcel(params: { from: string; to: string; departmentId?: string }) {
  const q = new URLSearchParams({ from: params.from, to: params.to })
  if (params.departmentId) q.set('departmentId', params.departmentId)
  return download(`/api/export/standard?${q.toString()}`, `출퇴근_${params.from}_${params.to}.xlsx`)
}

export function getAuditLogs(params: { action?: string; limit?: number; skip?: number } = {}) {
  const q = new URLSearchParams()
  if (params.action) q.set('action', params.action)
  q.set('limit', String(params.limit ?? 50))
  if (params.skip) q.set('skip', String(params.skip))
  return api<{ items: AuditLogItem[]; total: number }>(`/api/security/audit-logs?${q.toString()}`)
}

/**
 * 조직 설정 — ADMIN 전용 (서버도 403).
 *
 * ⚠️ judgmentMode 는 이 파일에서 **값으로만** 지나간다. 판정 분기는 하지 않는다.
 *    분기하는 곳은 AttendanceTime 하나뿐이다 (가드레일 #2).
 */
export interface OrgSettings {
  workStartTime: string
  workEndTime: string
  lateGraceMinutes: number
  earlyLeaveMinutes: number
  loginType: string
  /**
   * 🔴 직원을 만들 때 첫 비밀번호가 무엇이 되는지 (2026-09-09 추가).
   *
   *   EMPLOYEE_NUMBER   사번이 곧 비밀번호
   *   그 외(FALLBACK)   조직 공통 초기 비밀번호
   *
   * 서버는 원래 이 값을 보내고 있었는데 **화면이 타입에 안 담아서 못 읽었다.**
   * 그래서 직원 추가 화면이 조직 설정과 무관하게 «첫 비밀번호는 사번» 이라고 말했다.
   */
  initialPasswordType?: string
  uiConfig: OrgUiConfig
  /**
   * 휴가 종류별 회사 규정. 서버가 기본값 위에 저장값을 얹어 보낸다.
   *
   * 🔴 신청 화면은 이미 「회사가 정한 규정입니다」라고 말하는데 **정할 자리가 없었다**
   *   (2026-09-11). psql 을 쳐야 바뀌었다.
   */
  leaveConfig?: LeaveConfig
  judgmentMode: 'STANDARD' | 'FACTUAL'
  features: Record<string, boolean>
}

export function getOrgSettings() {
  return api<OrgSettings>('/api/organization/settings')
}

export function updateOrgSettings(body: Partial<OrgSettings>) {
  return api<unknown>('/api/organization/settings', {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}
